import axios, { AxiosRequestConfig } from 'axios';
import { BaseQueryFn, createApi } from '@reduxjs/toolkit/query/react';
import { getEnvironmentSettings } from '@utils/environmentConfig';
import { addToast } from '@base/components';
import {
    AnimationMode,
    AudioSynchronization,
    Coupon,
    CustomerType,
    GenerativeVideo,
    Genre,
    Order,
    Product,
    ProductType,
    Project,
    Resolution,
    Role,
    User,
} from '@base/types';

import { RootState } from '../types';
import { removeUser } from '../user';
import { BaseRequest } from './types';

const appConfig = getEnvironmentSettings();

const customBaseQuery: BaseQueryFn<BaseRequest, unknown, { status: number; data: string }> = async (
    { url = '', method = 'GET', headers, skipToken = false, baseUrl = appConfig.apiUrl, showErrorToast = true, ...otherConfigs },
    api,
) => {
    const { dispatch, getState } = api;

    try {
        // Get the authorization token from the Redux store
        const token = (getState() as RootState).user?.token;

        // Create the request configuration
        const config: AxiosRequestConfig = {
            url: `${baseUrl}${url}`,
            method,
            headers: {
                ...headers,
                ...(token && !skipToken ? { Authorization: `Bearer ${token}` } : {}),
            },
            ...otherConfigs,
        };

        // Perform the API call using axios
        const response = await axios(config);

        // Return the response data
        return {
            data: response.data,
        };
    } catch (error: any) {
        const errorData = error.response?.data;
        const status = error?.response?.status;

        // Handle unauthorized errors - silently redirect to login without showing error toast
        if (status === 401) {
            dispatch(removeUser());
        }

        // Only show error toast for non-401 errors (401 is expected behavior when token expires)
        if (showErrorToast && status !== 401) {
            // Show error toast with appropriate message
            const errorMessage = typeof errorData === 'object' ? errorData.message : errorData;
            addToast({
                variant: 'error',
                message: errorMessage || 'An error occurred',
            });
        }

        // Return error with original response data
        return {
            error: {
                status: status || 500,
                data: errorData,
            },
        };
    }
};

export const apiSlice = createApi({
    reducerPath: 'api',
    tagTypes: [
        'RESOLUTION',
        'PROJECT',
        'GENRE',
        'ANIMATION_MODE',
        'AUDIO_SYNCHRONIZATION',
        'PRODUCT_TYPE',
        'PRODUCT',
        'ORDER',
        'GENERATIVE_VIDEO',
        'CUSTOMER_TYPE',
        'USER',
        'ROLE',
        'COUPON',
        'REMAINING_MINUTES',
    ],
    baseQuery: customBaseQuery,
    endpoints: (builder) => ({
        completedProject: builder.query<Project[], void>({
            query: () => ({
                url: '/project/completed',
            }),
            providesTags: (projects) => [
                ...(projects ?? []).map((project) => ({ type: 'PROJECT' as const, id: `COMPLETED_${project.id}` })),
                { type: 'PROJECT', id: 'COMPLETED_all' },
            ],
        }),
        pendingProjects: builder.query<any[], void>({
            query: () => ({
                url: '/project/pending',
            }),
            providesTags: (projects) => [
                ...(projects ?? []).map((project) => ({ type: 'PROJECT' as const, id: `PENDING_${project.id}` })),
                { type: 'PROJECT', id: 'PENDING_all' },
            ],
        }),
        totalTiles: builder.query<
            {
                totalRemainingMinutes: number;
                totalUsedMinutes: number;
                totalProjects: number;
                totalUsers: number;
                currentPlan: string;
            },
            void
        >({
            query: () => ({
                url: '/userproject',
            }),
            transformResponse: (response: any) => ({
                ...response,
                currentPlan: response.subscriptionPlan || response.phoneNumber || 'Free Plan',
            }),
            providesTags: [{ type: 'PROJECT', id: 'total_tiles' }],
        }),
        twelveMonths: builder.query<any[], void>({
            query: () => ({
                url: '/userproject/last-twelve-months',
            }),
            providesTags: (projects) => [
                ...(projects ?? []).map((project) => ({ type: 'PROJECT' as const, id: `LAST_12_${project.id}` })),
                { type: 'PROJECT', id: 'LAST_12_all' },
            ],
        }),
        resetPassword: builder.mutation<any, any>({
            query: (data) => ({
                url: '/account/reset-password',
                data,
                method: 'POST',
            }),
        }),
        register: builder.mutation<
            { status: string },
            {
                firstName: string;
                lastName: string;
                customerTypeId: string;
                email: string;
                password: string;
                confirmPassword: string;
            }
        >({
            query: (data) => ({
                url: '/account/register',
                data: {
                    ...data,
                    roleId: 'a218865b-bcde-4a1f-8362-2c8bf5240515',
                },
                method: 'POST',
            }),
        }),
        activeCustomerTypes: builder.query<CustomerType[], void>({
            query: () => ({
                url: '/customertype/getactive',
            }),
            providesTags: (types) => [
                ...(types ?? []).map((type) => ({ type: 'CUSTOMER_TYPE' as const, id: type.id })),
                { type: 'CUSTOMER_TYPE', id: 'all' },
            ],
        }),
        customerTypes: builder.query<CustomerType[], void>({
            query: () => ({
                url: '/customertype',
            }),
            providesTags: (types) => [
                ...(types ?? []).map((type) => ({ type: 'CUSTOMER_TYPE' as const, id: type.id })),
                { type: 'CUSTOMER_TYPE', id: 'all' },
            ],
        }),
        putCustomerType: builder.mutation<unknown, Partial<CustomerType>>({
            query: (type) => ({ url: `/types/${type.id ?? ''}`, method: type.id ? 'PUT' : 'POST', data: type }),
            invalidatesTags: (result, response, data) => {
                const tags = [{ type: 'CUSTOMER_TYPE' as const, id: 'all' }];

                if (data.id) tags.unshift({ type: 'CUSTOMER_TYPE' as const, id: data.id });

                return tags;
            },
        }),
        customerType: builder.query<Genre, string>({
            query: (id) => ({ url: `/customertype/${id}` }),
            providesTags: (type) => (type ? [{ type: 'CUSTOMER_TYPE', id: type.id }] : []),
        }),
        deleteCustomerType: builder.mutation<unknown, string>({
            query: (id) => ({ url: `/customertype/${id}`, method: 'DELETE' }),
            invalidatesTags: (result, response, id) => [
                { type: 'CUSTOMER_TYPE', id },
                { type: 'CUSTOMER_TYPE', id: 'all' },
            ],
        }),
        login: builder.mutation<User, any>({
            query: (data) => ({
                url: '/account/login',
                data,
                method: 'POST',
                showErrorToast: false,
            }),
        }),
        verifyToken: builder.query<unknown, { userId: string; token: string }>({
            query: ({ userId, token }) => ({
                url: `/account/confirm-email?userId=${userId}&token=${token}`,
            }),
        }),
        resendEmail: builder.mutation<unknown, string>({
            query: (email) => ({
                url: `/account/resend-confirmation-email`,
                data: { email },
                method: 'POST',
            }),
        }),
        users: builder.query<any[], void>({
            query: () => ({
                url: '/users',
            }),
            transformResponse: (response: any[]) => {
                return response.map((user) => ({
                    ...user,
                    currentPlan: user.subscriptionPlan || user.phoneNumber || 'Free Plan',
                }));
            },
            providesTags: (users) => [
                ...(users ?? []).map((user) => ({ type: 'USER' as const, id: user.id })),
                { type: 'USER', id: 'all' },
            ],
        }),
        deleteUser: builder.mutation<unknown, string>({
            query: (id) => ({
                url: `/users/${id}`,
                method: 'DELETE',
            }),
            invalidatesTags: [{ type: 'USER', id: 'all' }],
        }),
        forgetPassword: builder.mutation<{ message: string }, string>({
            query: (email) => ({
                url: `/account/forget-password`,
                data: {
                    email,
                },
                method: 'POST',
            }),
        }),
        roles: builder.query<Role[], void>({
            query: () => ({ url: '/roles/getRoles' }),
            providesTags: (roles) => [
                ...(roles ?? []).map((role) => ({ type: 'ROLE' as const, id: role.id })),
                { type: 'ROLE', id: 'all' },
            ],
        }),
        user: builder.query<User, string>({
            query: (id) => ({ url: `/users/${id}` }),
            providesTags: (user) => (user ? [{ type: 'USER', id: user.id }] : []),
        }),
        putUser: builder.mutation<string, User>({
            query: (user) => ({ url: `/users/${user.id ?? ''}`, method: user.id ? 'PUT' : 'POST', data: user }),
            invalidatesTags: (result, response, data) => {
                const tags = [{ type: 'USER' as const, id: 'all' }];

                if (data.id) tags.unshift({ type: 'USER' as const, id: data.id });

                return tags;
            },
        }),
        resolutions: builder.query<Resolution[], void>({
            query: () => ({ url: `/resolution` }),
            providesTags: (resolutions) => [
                ...(resolutions ?? []).map((resolution) => ({ type: 'RESOLUTION' as const, id: resolution.id })),
                { type: 'RESOLUTION', id: 'all' },
            ],
        }),
        deleteResolution: builder.mutation<unknown, string>({
            query: (id) => ({ url: `/resolution/${id}`, method: 'DELETE' }),
            invalidatesTags: (result, response, id) => [
                { type: 'RESOLUTION', id },
                { type: 'RESOLUTION', id: 'all' },
            ],
        }),
        putResolution: builder.mutation<Resolution, Partial<Resolution>>({
            query: (data) => ({ url: `/resolution${data.id ? `/${data.id}` : ''}`, method: data.id ? 'PUT' : 'POST', data }),
            invalidatesTags: (result) => [
                { type: 'RESOLUTION', id: result?.id ?? '' },
                { type: 'RESOLUTION', id: 'all' },
            ],
        }),
        resolution: builder.query<Resolution, string>({
            query: (id) => ({ url: `/resolution/${id}` }),
            providesTags: (resolution) => (resolution ? [{ type: 'RESOLUTION', id: resolution.id }] : []),
        }),
        projects: builder.query<Project[], void>({
            query: () => ({ url: `/project` }),
            providesTags: (projects) => [
                ...(projects ?? []).map((project) => ({ type: 'PROJECT' as const, id: project.id })),
                { type: 'PROJECT', id: 'all' },
            ],
            transformResponse: (projects: Project[]) => {
                // Sort projects by creation date (newest first)
                const sortedProjects = projects.sort((a, b) => new Date(b.createDate).getTime() - new Date(a.createDate).getTime());
                // Add metadata about total count as a property of the array
                Object.defineProperty(sortedProjects, 'totalCount', {
                    enumerable: false,
                    value: projects.length,
                });
                return sortedProjects;
            },
            keepUnusedDataFor: 60,
        }),
        userProjects: builder.query<Project[], string>({
            query: (userId) => ({ url: `/project/user/${userId}` }),
            providesTags: (projects) => [
                ...(projects ?? []).map((project) => ({ type: 'PROJECT' as const, id: project.id })),
                { type: 'PROJECT', id: 'user' },
            ],
            transformResponse: (projects: Project[]) => {
                // Projects are already sorted by the backend (latest first)
                // Add metadata about total count as a property of the array
                Object.defineProperty(projects, 'totalCount', {
                    enumerable: false,
                    value: projects.length,
                });
                return projects;
            },
        }),
        deleteProject: builder.mutation<unknown, string>({
            query: (id) => ({ url: `/project/${id}`, method: 'DELETE' }),
            invalidatesTags: (result, response, id) => [
                { type: 'PROJECT', id },
                { type: 'PROJECT', id: 'all' },
            ],
        }),
        restartProject: builder.mutation<{ message: string; projectId: string; batchJobId: string; status: string }, string>({
            query: (id) => ({ url: `/project/${id}/restart`, method: 'POST' }),
            invalidatesTags: (result, response, id) => [
                { type: 'PROJECT', id },
                { type: 'PROJECT', id: 'all' },
            ],
        }),
        syncDurations: builder.mutation<
            {
                message: string;
                projectsChecked: number;
                projectsUpdated: number;
                projectsSkipped: number;
                updatedProjects: Array<{
                    projectId: string;
                    projectName: string;
                    tempAudioFileId: string;
                    previousMinutes: number;
                    newMinutes: number;
                    updatedAt: string;
                }>;
                timestamp: string;
            },
            void
        >({
            query: () => ({ url: `/project/sync-durations`, method: 'POST' }),
            invalidatesTags: [{ type: 'PROJECT', id: 'all' }],
        }),
        projectStatus: builder.mutation<unknown, { id: string; status: string }>({
            query: (data) => ({ url: `/project/${data.id}/admin-status`, method: 'PATCH', data: { statusId: data.status } }),
            // TODO: check if result is in <Project> type
            invalidatesTags: (result, response, data) => [
                { type: 'PROJECT', id: data.id },
                { type: 'PROJECT', id: 'all' },
            ],
        }),
        putProjectByStep: builder.mutation<Project, { data: Partial<Project>; step?: number; submit?: boolean }>({
            query: ({ step, data, submit }) => ({
                url: submit ? `/project/submit/${data.id}` : `/project/step${step}${data.id ? `/${data.id}` : ''}`,
                data,
                method: data.id ? 'PUT' : 'POST',
            }),
            invalidatesTags: (result) => [
                { type: 'PROJECT', id: result?.id ?? '' },
                { type: 'PROJECT', id: 'all' },
            ],
        }),
        project: builder.query<Project, string>({
            query: (id) => ({ url: `/project/${id}` }),
            providesTags: (project) => (project ? [{ type: 'PROJECT', id: project.id }] : []),
        }),
        genres: builder.query<Genre[], void>({
            query: () => ({ url: `/genre` }),
            providesTags: (genres) => [
                ...(genres ?? []).map((genre) => ({ type: 'GENRE' as const, id: genre.id })),
                { type: 'GENRE', id: 'all' },
            ],
            transformResponse: (genresResponse: Genre[]) => {
                const genres = genresResponse.filter((genre) => genre.name !== 'Other');
                const otherGenre = genresResponse.find((genre) => genre.name === 'Other');

                if (otherGenre) genres.push(otherGenre);

                return genres;
            },
        }),
        deleteGenre: builder.mutation<unknown, string>({
            query: (id) => ({ url: `/genre/${id}`, method: 'DELETE' }),
            invalidatesTags: (result, response, id) => [
                { type: 'GENRE', id },
                { type: 'GENRE', id: 'all' },
            ],
        }),
        putGenre: builder.mutation<Genre, Partial<Genre>>({
            query: (data) => ({ url: `/genre${data.id ? `/${data.id}` : ''}`, method: data.id ? 'PUT' : 'POST', data }),
            invalidatesTags: (genre) => [
                { type: 'GENRE', id: genre?.id ?? '' },
                { type: 'GENRE', id: 'all' },
            ],
        }),
        genre: builder.query<Genre, string>({
            query: (id) => ({ url: `/genre/${id}` }),
            providesTags: (genre) => (genre ? [{ type: 'GENRE', id: genre.id }] : []),
        }),
        animationModes: builder.query<AnimationMode[], void>({
            query: () => ({ url: `/animationmode` }),
            providesTags: (modes) => [
                ...(modes ?? []).map((mode) => ({ type: 'ANIMATION_MODE' as const, id: mode.id })),
                { type: 'ANIMATION_MODE', id: 'all' },
            ],
        }),
        deleteAnimationMode: builder.mutation<unknown, string>({
            query: (id) => ({ url: `/animationmode/${id}`, method: 'DELETE' }),
            invalidatesTags: (result, response, id) => [
                { type: 'ANIMATION_MODE', id },
                { type: 'ANIMATION_MODE', id: 'all' },
            ],
        }),
        putAnimationMode: builder.mutation<AnimationMode, Partial<AnimationMode>>({
            query: (data) => ({ url: `/animationmode${data.id ? `/${data.id}` : ''}`, method: data.id ? 'PUT' : 'POST', data }),
            invalidatesTags: (mode) => [
                { type: 'ANIMATION_MODE', id: mode?.id ?? '' },
                { type: 'ANIMATION_MODE', id: 'all' },
            ],
        }),
        animationMode: builder.query<AnimationMode, string>({
            query: (id) => ({ url: `/animationmode/${id}` }),
            providesTags: (mode) => (mode ? [{ type: 'ANIMATION_MODE', id: mode.id }] : []),
        }),
        activeAudioSynchronizations: builder.query<AudioSynchronization[], void>({
            query: () => ({ url: `/audiosynchronization/getActive` }),
            providesTags: (modes) => [
                ...(modes ?? []).map((mode) => ({ type: 'AUDIO_SYNCHRONIZATION' as const, id: mode.id })),
                { type: 'AUDIO_SYNCHRONIZATION', id: 'all' },
            ],
        }),
        activeResolutions: builder.query<Resolution[], void>({
            query: () => ({ url: `/resolution/getActive` }),
            providesTags: (resolutions) => [
                ...(resolutions ?? []).map((resolution) => ({ type: 'RESOLUTION' as const, id: resolution.id })),
                { type: 'RESOLUTION', id: 'active_all' },
            ],
        }),
        activeGenres: builder.query<Genre[], void>({
            query: () => ({ url: `/genre/getActive` }),
            providesTags: (genres) => [
                ...(genres ?? []).map((genre) => ({ type: 'GENRE' as const, id: genre.id })),
                { type: 'GENRE', id: 'active_all' },
            ],
        }),
        activeAnimationModes: builder.query<AnimationMode[], void>({
            query: () => ({ url: `/animationmode/getActive` }),
            providesTags: (modes) => [
                ...(modes ?? []).map((mode) => ({ type: 'ANIMATION_MODE' as const, id: mode.id })),
                { type: 'ANIMATION_MODE', id: 'active_all' },
            ],
        }),
        activeProductTypes: builder.query<ProductType[], void>({
            query: () => ({ url: `/producttype/getActive` }),
            providesTags: (types) => [
                ...(types ?? []).map((type) => ({ type: 'PRODUCT_TYPE' as const, id: type.id })),
                { type: 'PRODUCT_TYPE', id: 'active_all' },
            ],
        }),
        activeGenerativeVideos: builder.query<GenerativeVideo[], void>({
            query: () => ({ url: `/generativevideointegration/getActive` }),
            providesTags: (videos) => [
                ...(videos ?? []).map((video) => ({ type: 'GENERATIVE_VIDEO' as const, id: video.id })),
                { type: 'GENERATIVE_VIDEO', id: 'active_all' },
            ],
        }),
        audioSynchronizations: builder.query<AudioSynchronization[], void>({
            query: () => ({ url: `/audiosynchronization` }),
            providesTags: (modes) => [
                ...(modes ?? []).map((mode) => ({ type: 'AUDIO_SYNCHRONIZATION' as const, id: mode.id })),
                { type: 'AUDIO_SYNCHRONIZATION', id: 'all' },
            ],
        }),
        deleteAudioSynchronization: builder.mutation<unknown, string>({
            query: (id) => ({ url: `/audiosynchronization/${id}`, method: 'DELETE' }),
            invalidatesTags: (result, response, id) => [
                { type: 'AUDIO_SYNCHRONIZATION', id },
                { type: 'AUDIO_SYNCHRONIZATION', id: 'all' },
            ],
        }),
        putAudioSynchronization: builder.mutation<AudioSynchronization, Partial<AudioSynchronization>>({
            query: (data) => ({ url: `/audiosynchronization${data.id ? `/${data.id}` : ''}`, method: data.id ? 'PUT' : 'POST', data }),
            invalidatesTags: (audio) => [
                { type: 'AUDIO_SYNCHRONIZATION', id: audio?.id ?? '' },
                { type: 'AUDIO_SYNCHRONIZATION', id: 'all' },
            ],
        }),
        audioSynchronization: builder.query<AudioSynchronization, string>({
            query: (id) => ({ url: `/audiosynchronization/${id}` }),
            providesTags: (audio) => (audio ? [{ type: 'AUDIO_SYNCHRONIZATION', id: audio.id }] : []),
        }),
        presignedUrl: builder.mutation<string, string>({
            query: (filename) => ({
                url: `get-presigned-url`,
                baseUrl: appConfig.blob.baseUrl,
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                data: { filename },
            }),
        }),
        upload: builder.mutation<void, { file: File; presignedUrl: string } & Pick<AxiosRequestConfig, 'onUploadProgress'>>({
            query: ({ presignedUrl, file, onUploadProgress }) => ({
                baseUrl: presignedUrl,
                method: 'PUT',
                data: file,
                headers: { 'Content-Type': file.type },
                onUploadProgress,
                skipToken: true,
            }),
        }),
        productType: builder.query<ProductType, string>({
            query: (id) => ({ url: `/producttype/${id}` }),
            providesTags: (productType) => (productType ? [{ type: 'PRODUCT_TYPE', id: productType.id }] : []),
        }),
        putProductType: builder.mutation<ProductType, Partial<Product>>({
            query: (data) => ({
                url: `/producttype${data.id ? `/${data.id}` : ''}`,
                method: data.id ? 'PUT' : 'POST',
                data,
            }),
            invalidatesTags: (result) => [
                { type: 'PRODUCT_TYPE', id: result?.id ?? '' },
                { type: 'PRODUCT_TYPE', id: 'all' },
            ],
        }),
        productTypes: builder.query<ProductType[], void>({
            query: () => ({ url: `/producttype` }),
            providesTags: (types) => [
                ...(types ?? []).map((type) => ({ type: 'PRODUCT_TYPE' as const, id: type.id })),
                { type: 'PRODUCT_TYPE', id: 'all' },
            ],
        }),
        deleteProductType: builder.mutation<unknown, string>({
            query: (id) => ({ url: `/producttype/${id}`, method: 'DELETE' }),
            invalidatesTags: (result, response, id) => [
                { type: 'PRODUCT_TYPE', id },
                { type: 'PRODUCT_TYPE', id: 'all' },
            ],
        }),
        products: builder.query<Product[], void>({
            query: () => ({ url: `/products` }),
            providesTags: (products) => [
                ...(products ?? []).map((product) => ({ type: 'PRODUCT' as const, id: product.id })),
                { type: 'PRODUCT', id: 'all' },
            ],
        }),
        deleteProduct: builder.mutation<unknown, string>({
            query: (id) => ({ url: `/products/${id}`, method: 'DELETE' }),
            invalidatesTags: (result, response, id) => [
                { type: 'PRODUCT', id },
                { type: 'PRODUCT', id: 'all' },
            ],
        }),
        putProduct: builder.mutation<Product, Partial<Product>>({
            query: (data) => ({
                url: `/products${data.id ? `/${data.id}` : ''}`,
                method: data.id ? 'PUT' : 'POST',
                data,
            }),
            invalidatesTags: (result) => [
                { type: 'PRODUCT', id: result?.id ?? '' },
                { type: 'PRODUCT', id: 'all' },
            ],
        }),
        product: builder.query<Product, string>({
            query: (id) => ({ url: `/products/${id}` }),
            providesTags: (product) => (product ? [{ type: 'PRODUCT' as const, id: product.id }] : []),
        }),
        clientProducts: builder.query<Product[], void>({
            query: () => ({ url: `/products/get-active` }),
            providesTags: (products) => [
                ...(products ?? []).map((product) => ({ type: 'PRODUCT' as const, id: product.id })),
                { type: 'PRODUCT', id: 'all' },
            ],
        }),
        orders: builder.query<Order[], void>({
            query: () => ({ url: `/order` }),
            providesTags: (orders) => [
                ...(orders ?? []).map((order) => ({ type: 'ORDER' as const, id: order.id })),
                { type: 'ORDER', id: 'all' },
            ],
        }),
        generativeVideos: builder.query<GenerativeVideo[], void>({
            query: () => ({ url: `/generativevideointegration` }),
            providesTags: (videos) => [
                ...(videos ?? []).map((video) => ({ type: 'GENERATIVE_VIDEO' as const, id: video.id })),
                { type: 'GENERATIVE_VIDEO', id: 'all' },
            ],
        }),
        deleteGenerativeVideo: builder.mutation<unknown, string>({
            query: (id) => ({ url: `/generativevideointegration/${id}`, method: 'DELETE' }),
            invalidatesTags: (result, response, id) => [
                { type: 'GENERATIVE_VIDEO', id },
                { type: 'GENERATIVE_VIDEO', id: 'all' },
            ],
        }),
        generativeVideo: builder.query<GenerativeVideo, string>({
            query: (id) => ({ url: `/generativevideointegration/${id}` }),
            providesTags: (video) => (video ? [{ type: 'GENERATIVE_VIDEO', id: video.id }] : []),
        }),
        putGenerativeVideo: builder.mutation<GenerativeVideo, Partial<GenerativeVideo>>({
            query: (data) => ({
                url: `/generativevideointegration${data.id ? `/${data.id}` : ''}`,
                method: data.id ? 'PUT' : 'POST',
                data,
            }),
            invalidatesTags: (result) => [
                { type: 'GENERATIVE_VIDEO', id: result?.id ?? '' },
                { type: 'GENERATIVE_VIDEO', id: 'all' },
            ],
        }),
        coupons: builder.query<Coupon[], void>({
            query: () => ({ url: `/coupon` }),
            providesTags: (coupons) => [
                ...(coupons ?? []).map((coupon) => ({ type: 'COUPON' as const, id: coupon.id })),
                { type: 'COUPON', id: 'all' },
            ],
        }),
        deleteCoupon: builder.mutation<unknown, string>({
            query: (id) => ({ url: `/coupon/${id}`, method: 'DELETE' }),
            invalidatesTags: (result, response, id) => [
                { type: 'COUPON', id },
                { type: 'COUPON', id: 'all' },
            ],
        }),
        putCoupon: builder.mutation<Coupon, Partial<Coupon>>({
            query: (data) => ({ url: `/coupon${data.id ? `/${data.id}` : ''}`, method: data.id ? 'PUT' : 'POST', data }),
            invalidatesTags: (coupon) => [
                { type: 'COUPON', id: coupon?.id ?? '' },
                { type: 'COUPON', id: 'all' },
            ],
        }),
        coupon: builder.query<Coupon, string>({
            query: (id) => ({ url: `/coupon/${id}` }),
            providesTags: (coupon) => (coupon ? [{ type: 'COUPON', id: coupon.id }] : []),
        }),
        checkout: builder.query<{ orderNumber: string; productName: string }, string>({
            query: (sessionId) => ({ url: `/order/checkout-complete`, method: 'POST', data: { sessionId } }),
        }),
        authByGoogle: builder.mutation<{ message: string; success: boolean; user: User }, { credential: string; referrerId?: string }>({
            query: (data) => ({
                method: 'POST',
                url: '/account/google-oauth',
                data,
            }),
        }),
        deleteSubscription: builder.mutation<void, void>({
            query: () => ({
                url: '/order/switch-to-free-plan',
                method: 'POST',
            }),
        }),
        remainingMinutes: builder.query<
            {
                remainingMinutes: number;
                totalMinutes: number;
                subscriptionMinutes: number;
                accruedMinutes: number;
                blockedMinutes: number;
                actualSubscriptionMinutes: number;
                actualAccruedMinutes: number;
                currentPlan: string;
                currentPeriodStart: string;
                currentPeriodEnd: string;
            },
            void
        >({
            query: () => ({
                url: '/order/remaining-minutes',
            }),
            providesTags: ['REMAINING_MINUTES'],
        }),
        getStripeCustomerId: builder.query<{ customerId: string }, void>({
            query: () => ({
                url: '/order/stripe-customer-id',
            }),
        }),
        getProductPrice: builder.query<{ price: number }, string>({
            query: (productName) => ({
                url: `/order/product-price/${productName}`,
                method: 'GET',
            }),
            transformResponse: (response: { amount: number }) => ({
                price: response.amount,
            }),
        }),
        getRemainingMinutesForUser: builder.query<
            {
                remainingMinutes: number;
                totalMinutes: number;
                subscriptionMinutes: number;
                accruedMinutes: number;
                blockedMinutes: number;
                actualSubscriptionMinutes: number;
                actualAccruedMinutes: number;
            },
            string
        >({
            query: (userId) => ({ url: `/order/remaining-minutes/${userId}` }),
            providesTags: ['REMAINING_MINUTES'],
        }),
        getInnovatorDiscount: builder.query<
            {
                discountAmount: number;
                discountAmountCents: number;
                subscriptionMinutes: number;
                hasDiscount: boolean;
            },
            void
        >({
            query: () => ({
                url: '/order/innovator-discount',
                method: 'GET',
            }),
        }),
        addUserMinute: builder.mutation<{ success: boolean; message: string; newTotalMinutes: number }, string>({
            query: (userId) => ({
                url: `/order/add-minute/${userId}`,
                method: 'POST',
            }),
            invalidatesTags: ['REMAINING_MINUTES', { type: 'USER', id: 'all' }],
        }),
        createCheckoutSession: builder.mutation<{ url: string }, { productName: string; mode: 'payment' | 'subscription' }>({
            query: (data) => ({
                url: '/order/create-checkout-session',
                method: 'POST',
                data,
            }),
        }),
        extractLyrics: builder.mutation<
            {
                success: boolean;
                timestampedLyrics: Array<{ text: string; start: number; end: number }>;
                allLyricsText: string;
                segments: Array<{ start: number; end: number; lyrics: string; energy: number }>;
                errorMessage?: string;
            },
            { projectId: string; outputDir?: string; debug?: boolean }
        >({
            query: ({ projectId, outputDir, debug = false }) => ({
                url: `/api/LyricsExtraction/extract-from-project/${projectId}${outputDir || debug ? '?' : ''}${outputDir ? `outputDir=${outputDir}` : ''}${outputDir && debug ? '&' : ''}${debug ? `debug=${debug}` : ''}`,
                method: 'POST',
            }),
        }),
        saveLyricsModifications: builder.mutation<
            { success: boolean; message: string },
            {
                projectId: string;
                timestampedLyrics: Array<{
                    Word: string;
                    Start: number;
                    End: number;
                    Score: number;
                }>;
            }
        >({
            query: ({ projectId, timestampedLyrics }) => ({
                url: '/api/LyricsExtraction/save-lyrics',
                method: 'POST',
                data: {
                    projectId,
                    timestampedLyrics,
                },
            }),
        }),
        editImage: builder.mutation<
            {
                success: boolean;
                message?: string;
                image?: number[];
                originalPrompt?: string;
            },
            {
                imageUrl?: string;
                imageData?: string;
                imageUrls?: string[];
                imageDataArray?: string[];
                prompt: string;
                size?: string;
                n?: number;
            }
        >({
            query: (data) => ({
                url: '/api/StoryCreator/edit-image',
                method: 'POST',
                data,
                showErrorToast: false, // We'll handle errors in the component
            }),
        }),
        editImageWithPiApi: builder.mutation<
            {
                success: boolean;
                message?: string;
                imageUrl?: string;
                originalPrompt?: string;
                provider?: string;
            },
            {
                imageUrl?: string;
                imageData?: string;
                prompt: string;
                size?: string;
            }
        >({
            query: (data) => ({
                url: '/api/StoryCreator/edit-image-piapi',
                method: 'POST',
                data,
                showErrorToast: false, // We'll handle errors in the component
            }),
        }),
        uploadBackgroundMusic: builder.mutation<
            {
                success: boolean;
                message: string;
                url: string;
            },
            {
                projectId: string;
                file: File;
            }
        >({
            query: ({ projectId, file }) => {
                const formData = new FormData();
                formData.append('file', file);

                return {
                    url: `/upload-background-music/${projectId}`,
                    method: 'POST',
                    data: formData,
                    headers: {
                        // Let browser set Content-Type with boundary for multipart/form-data
                    },
                    showErrorToast: false, // We'll handle errors in the component
                };
            },
        }),
    }),
});

export const {
    useCompletedProjectQuery,
    usePendingProjectsQuery,
    useTotalTilesQuery,
    useTwelveMonthsQuery,
    useResetPasswordMutation,
    useActiveCustomerTypesQuery,
    useRegisterMutation,
    useLoginMutation,
    useLazyVerifyTokenQuery,
    useResendEmailMutation,
    useUsersQuery,
    useDeleteUserMutation,
    useForgetPasswordMutation,
    useLazyUserQuery,
    useRolesQuery,
    usePutUserMutation,
    useResolutionsQuery,
    useDeleteResolutionMutation,
    usePutResolutionMutation,
    useLazyResolutionQuery,
    useProjectsQuery,
    useUserProjectsQuery,
    useProjectStatusMutation,
    useProjectQuery,
    usePutProjectByStepMutation,
    usePresignedUrlMutation,
    useUploadMutation,
    useGenresQuery,
    useActiveAudioSynchronizationsQuery,
    useActiveResolutionsQuery,
    useActiveGenresQuery,
    useActiveAnimationModesQuery,
    useActiveProductTypesQuery,
    useActiveGenerativeVideosQuery,
    usePutProductTypeMutation,
    useProductTypeQuery,
    useProductTypesQuery,
    useDeleteProductTypeMutation,
    useProductsQuery,
    useDeleteProductMutation,
    useProductQuery,
    usePutProductMutation,
    useClientProductsQuery,
    useOrdersQuery,
    useDeleteGenreMutation,
    usePutGenreMutation,
    useGenreQuery,
    useGenerativeVideosQuery,
    useDeleteGenerativeVideoMutation,
    useGenerativeVideoQuery,
    usePutGenerativeVideoMutation,
    useCustomerTypesQuery,
    useDeleteCustomerTypeMutation,
    useCustomerTypeQuery,
    usePutCustomerTypeMutation,
    useCouponsQuery,
    useDeleteCouponMutation,
    useCouponQuery,
    usePutCouponMutation,
    useCheckoutQuery,
    useAudioSynchronizationsQuery,
    useDeleteAudioSynchronizationMutation,
    usePutAudioSynchronizationMutation,
    useAudioSynchronizationQuery,
    useAnimationModesQuery,
    useDeleteAnimationModeMutation,
    usePutAnimationModeMutation,
    useAnimationModeQuery,
    useAuthByGoogleMutation,
    useDeleteSubscriptionMutation,
    useAddUserMinuteMutation,
    useRemainingMinutesQuery,
    useDeleteProjectMutation,
    useRestartProjectMutation,
    useSyncDurationsMutation,
    useGetStripeCustomerIdQuery,
    useGetProductPriceQuery,
    useLazyGetRemainingMinutesForUserQuery,
    useGetInnovatorDiscountQuery,
    useCreateCheckoutSessionMutation,
    useExtractLyricsMutation,
    useSaveLyricsModificationsMutation,
    useEditImageMutation,
    useEditImageWithPiApiMutation,
    useUploadBackgroundMusicMutation,
} = apiSlice;
